/* eslint-disable no-nested-ternary */
/* eslint-disable prefer-reflect */
/* eslint-disable kf/const-naming-convention */
import { useState, useEffect } from "react";
import { t } from "@lingui/macro";
import diacritics from "diacritics";
import { cloneDeep as lodashCloneDeep } from "lodash-es";

import {
  DataService,
  PublicService
} from "@base/common/libs/services/index.js";
import {
  FLOW_TYPE,
  ENV,
  WORK_PLATFORM_FEATURE_FLAGS,
  ENVIRONMENT_TYPES,
  DEFAULT_FIELD_LIMIT,
  DEFAULT_FORM_META_LIMIT,
  OLD_TO_NEW_COLOR_MAPPING,
  WIDGET_TYPES,
  SUB_FLOW_TYPES
} from "@base/common/constants/index.js";
import { getErrorText } from "../../messages/error.message.js";
import { useFilePreviewStore } from "../stores/file.preview.store.js";
import { useAccountStore, useUserStore } from "../stores/index.js";
import { getS3FileKey } from "./attachment.js";
import { i18n } from "@base/common/libs/i18n/index.js";

export const fieldsCache = {};
export const fieldsWithInternalCache = {};

export async function getSystemFields(
  modelId = "System",
  serviceName = "User",
  refetch = false
) {
  const accountId = window.accountDetails._id;
  let APIService = DataService;
  let options = {};
  try {
    if (fieldsCache[modelId] && !refetch) {
      return fieldsCache[modelId];
    }
    serviceName = serviceName.toLowerCase();
    let url = `/${serviceName}/2/${accountId}/system/${modelId}/fields`;
    if (window.location.pathname.startsWith("/public/")) {
      url = `/${serviceName}/2/${accountId}/publicform/system/${modelId}/fields`;
      APIService = PublicService;
      options = {
        recaptcha_action: "getSysytemFields"
      };
    }
    if (modelId === "User") {
      url = `/user/2/${accountId}/permissible/fields`;
    }
    let [promise] = APIService.get(url, {
      options
    });
    let fieldList = await promise().then((fields) => fields);
    fieldsWithInternalCache[modelId] = fieldList;
    let fieldsWithoutInternal = fieldList.filter(
      (field) => field.IsInternal !== true
    );
    fieldsCache[modelId] = fieldsWithoutInternal;
    return fieldsWithoutInternal;
  } catch (err) {
    console.warn("Failed to get system fields", err);
    return [];
  }
}

export async function getReservedKeywords(
  modelId = "System",
  serviceName,
  refetch = false
) {
  const accountId = window.accountDetails._id;
  try {
    if (fieldsWithInternalCache[modelId] && !refetch) {
      return fieldsWithInternalCache[modelId];
    }
    if (!serviceName) {
      return [];
    }
    serviceName = serviceName.toLowerCase();
    let url = `/${serviceName}/2/${accountId}/reserved/keywords`;
    let [promise] = DataService.get(url, {
      options: {}
    });
    let fieldList = await promise().then((fields) => fields);
    fieldsWithInternalCache[modelId] = fieldList;
    return fieldList;
  } catch (err) {
    console.warn("Failed to get reserved keywords", err);
    return [];
  }
}

export function getEpochString({ timestamp, short = false }) {
  // Epochs
  const epochs = short
    ? [
        ["y", 31536000],
        ["mo", 2592000],
        ["w", 604800],
        ["d", 86400],
        ["h", 3600],
        ["min", 60]
      ]
    : [
        ["year", 31536000],
        ["month", 2592000],
        ["week", 604800],
        ["day", 86400],
        ["hour", 3600],
        ["minute", 60]
      ];

  const singularEpochs = {
    year: t`year`,
    month: t`month`,
    week: t`week`,
    day: t`day`,
    hour: t`hour`,
    minute: t`minute`,
    y: t`y`,
    mo: t`mo`,
    w: t`w`,
    d: t`d`,
    h: t`h`,
    min: t`min`
  };

  const pluralEpochs = {
    year: t`years`,
    month: t`months`,
    week: t`weeks`,
    day: t`days`,
    hour: t`hours`,
    minute: t`minutes`,
    y: t`y`,
    mo: t`mo`,
    w: t`w`,
    d: t`d`,
    h: t`h`,
    min: t`min`
  };

  // Get duration
  const getDuration = (timeAgoInSeconds) => {
    for (let [name, seconds] of epochs) {
      const interval = Math.floor(timeAgoInSeconds / seconds);

      if (interval >= 1) {
        return {
          interval: interval,
          epoch: name
        };
      }
    }
  };

  const timeAgoInSeconds = Math.floor(
    (new Date() - new Date(timestamp)) / 1000
  );
  const epochDetail = getDuration(timeAgoInSeconds);
  if (!epochDetail) {
    return t`Now`;
  }
  let { interval, epoch } = getDuration(timeAgoInSeconds);
  epoch = interval === 1 ? singularEpochs[epoch] : pluralEpochs[epoch];
  return short ? `${interval}${epoch}` : `${interval} ${epoch} ${t`ago`}`;
}

export const bytesToSize = (bytes, decimals = 0, space = false) => {
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return space
    ? parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
    : parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i];
};

export const getModelFields = async (model) => {
  try {
    let parentModel = model.getParentModel();
    //parentFlow getFields request should go to respective flow services but
    //except project/case it goes to process service
    let serviceName = [FLOW_TYPE.PROJECT, FLOW_TYPE.CASE].includes(
      model.getFlowType()
    )
      ? model.getFlowType()
      : FLOW_TYPE.PROCESS;
    let systemFields = await getSystemFields(parentModel, serviceName);
    let modelFields = model.getList("Model", "Field");
    let excludedFields = [
      "_id",
      "_is_deleted",
      "_root_process_instance",
      "_current_assigned_to",
      "_restart_info",
      "_progress",
      "_note",
      "_triggered_integrations",
      "_current_step",
      "_last_completed_step",
      //project fields
      "_stage",
      "_state",
      "_resolution"
    ];
    systemFields = systemFields.filter(
      (field) => !excludedFields.includes(field.Id)
    );
    return systemFields.concat(
      modelFields.map((field) => ({
        Id: field.getValue("Id"),
        Type: field.getValue("Type"),
        Name: field.getValue("Name")
      }))
    );
  } catch (err) {
    console.error(err);
    return [];
  }
};

export function values(obj) {
  const result = [];
  for (const k in obj) {
    result.push(obj[k]);
  }
  return result;
}

export function isProductionEnv() {
  return window.accountDetails.AppConfig.BaseEnv === ENV.PRD;
}

export function shouldAllowSupportChannel() {
  let preference = useAccountStore.getState().Preference || {};
  let disableExternalService = preference._disable_external_service;

  if (disableExternalService) {
    return false;
  }

  return [ENV.PRD, ENV.UAT].includes(getEnv());
}

export function getEnv() {
  return isProductionEnv() ? ENV.PRD : window.accountDetails.AppConfig.Env;
}

export const isLocalhost = function isLocalHostDerived() {
  return Boolean(
    window.location.hostname === "localhost" ||
      // localhost with subdomain
      window.location.hostname.split(".")[1] === "localhost" ||
      // [::1] is the IPv6 localhost address.
      window.location.hostname === "[::1]" ||
      // 127.0.0.1/8 is considered localhost for IPv4.
      // Also Regex added for ip address starting with 192, so that Ip can access localhost in mobile devices
      window.location.hostname.match(
        /^(127|192)(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
      )
  );
};

export function debounce(func, wait, immediate) {
  var timeout;
  function debounceWrapper() {
    // eslint-disable-next-line consistent-this
    var context = this;
    var args = arguments;
    clearTimeout(timeout);
    timeout = setTimeout(function settingTimeOut() {
      timeout = null;
      if (!immediate) func.apply(context, args);
    }, wait);
    if (immediate && !timeout) func.apply(context, args);
  }
  debounceWrapper.cancel = () => {
    clearTimeout(timeout);
  };
  return debounceWrapper;
}

export function throttle(func, wait) {
  var waiting = false;
  return function gettingThrootle() {
    if (!waiting) {
      func.apply(this, arguments);
      waiting = true;
      setTimeout(function settingThrottleTimeout() {
        waiting = false;
      }, wait);
    }
  };
}

export const convertAndAddUnitToFileSize = (fileSize) => {
  if (fileSize < 1024) {
    //file size is less than 1kB
    return `${fileSize}B`;
  }
  let fizeSize = (fileSize / (1024 * 1024)).toFixed(2);
  if (fizeSize >= 1) {
    //file size is greater than and equal to 1MB
    return `${fizeSize}MB`;
  }
  //file size is less than 1MB and greater than to 1KB
  fileSize = ((fileSize / (1024 * 1024)) * 1024).toFixed(2);
  return `${fileSize}KB`;
};

export function isFunction(func) {
  return typeof func === "function";
}

function omitByPickByFilter(obj, fn) {
  const entries = Object.entries(obj).filter(fn);
  return Object.fromEntries(entries);
}

export const pickBy = (obj = {}, fn = () => {}) =>
  omitByPickByFilter(obj, ([key, val]) => fn(val, key));
export const omitBy = (obj = {}, fn = () => {}) =>
  omitByPickByFilter(obj, ([key, val]) => !fn(val, key));

export const cloneDeep = (obj) => JSON.parse(JSON.stringify(obj));

export function cloneInstanceObject(oldobject) {
  // return Object.assign(Object.create(Reflect.getPrototypeOf(oldobject)), oldobject);

  // if (typeof oldobject !== "object" || oldobject === null) {
  //   return oldobject;
  // }

  // if (oldobject instanceof Date) {
  //   return new Date(oldobject.getTime());
  // }

  // if (oldobject instanceof Array) {
  //   return oldobject.reduce((arr, item, i) => {
  //     arr[i] = cloneInstanceObject(item);
  //     return arr;
  //   }, []);
  // }

  // if (oldobject instanceof Object) {
  //   return Object.keys(oldobject).reduce((newObj, key) => {
  //     newObj[key] = cloneInstanceObject(oldobject[key]);
  //     return newObj;
  //   }, Object.assign(Object.create(Reflect.getPrototypeOf(oldobject)), {}));
  // }
  return lodashCloneDeep(oldobject);
}

export function get(obj, string = "", defaultValue = "") {
  // eslint-disable-next-line no-useless-escape
  const parts = string.split(/[\]\[\.]/).filter((x) => x);
  let attempt = obj;
  for (let part of parts) {
    if (attempt == null || attempt[part] === undefined) return defaultValue;
    attempt = attempt[part];
  }
  return attempt;
}

export function differenceBy(array1 = [], array2 = [], key = "") {
  return array1.filter((a) => !array2.some((b) => b[key] === a[key]));
}

const sortByData = (key) => {
  return [Function].includes(key.constructor)
    ? (a, b) => (key(a) > key(b) ? 1 : key(b) > key(a) ? -1 : 0)
    : (a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0);
};
export const sortBy = (array = [], key) => array.concat().sort(sortByData(key));

export const identity = (a) => a;

export const isEqual = (value, other) => {
  if (value === other) {
    return true;
  }
  // Get the value type
  var type = Object.prototype.toString.call(value);

  // If the two objects are not the same type, return false
  if (type !== Object.prototype.toString.call(other)) return false;

  // If items are not an object or array, return false
  if (["[object Array]", "[object Object]"].indexOf(type) < 0) return false;

  // Compare the length of the length of the two items
  var valueLen =
    type === "[object Array]" ? value.length : Object.keys(value).length;
  var otherLen =
    type === "[object Array]" ? other.length : Object.keys(other).length;
  if (valueLen !== otherLen) return false;

  // Compare two items
  var compare = (item1, item2) => {
    // Get the object type
    var itemType = Object.prototype.toString.call(item1);

    // If an object or array, compare recursively
    if (["[object Array]", "[object Object]"].indexOf(itemType) >= 0) {
      if (!isEqual(item1, item2)) return false;
    }

    // Otherwise, do a simple comparison
    else {
      // If the two items are not the same type, return false
      if (itemType !== Object.prototype.toString.call(item2)) return false;

      // Else if it's a function, convert to a string and compare
      // Otherwise, just compare
      if (itemType === "[object Function]") {
        if (item1.toString() !== item2.toString()) return false;
      } else if (item1 !== item2) {
        return false;
      }
    }
  };

  // Compare properties
  if (type === "[object Array]") {
    for (var i = 0; i < valueLen; i++) {
      if (compare(value[i], other[i]) === false) return false;
    }
  } else {
    for (var key in value) {
      if (value.hasOwnProperty(key)) {
        if (compare(value[key], other[key]) === false) return false;
      }
    }
  }

  // If nothing failed, return true
  return true;
};

export const isEqualWith = (value, other, customizer = () => {}) => {
  const comparator = customizer(value, other);
  return comparator ?? isEqual(value, other);
};

export function memoize(fn = () => {}, resolver) {
  const cache = new Map();
  // instead of returning the function right away, store it in a variable...
  const memoized = (...args) => {
    // eslint-disable-next-line prefer-spread
    const key = resolver ? resolver.apply(null, args) : args[0];
    if (cache.has(key)) {
      return cache.get(key);
    } else {
      // eslint-disable-next-line prefer-spread
      const result = fn.apply(null, args);
      cache.set(key, result);
      return result;
    }
  };
  // add a method to it to get the cache
  memoized.getCache = () => cache;
  // now return the function
  return memoized;
}

// export const differenceWith = (sourceArr, otherArr, comparater = () => {}) => {
//   let stringDict = otherArr.reduce((cumulater, value) => {
//     let str = JSON.stringify(value);
//     cumulater[str] = str;
//     return cumulater;
//   }, {});
//   let difference = sourceArr.filter((val) => {
//     return !stringDict[JSON.stringify(val)];
//   });
//   return difference;
// };

export const differenceWith = (sourceArr, otherArr, comparater = () => {}) => {
  return [sourceArr, otherArr].reduce((cumulater, value) =>
    cumulater.filter((val) => {
      return !value.find(
        (data) =>
          comparater(data, val) ?? JSON.stringify(data) === JSON.stringify(val)
      );
    })
  );
};

export const findIndex = (sourceArr, value, prop) => {
  return sourceArr.findIndex((element) =>
    prop
      ? element[prop] === value[prop]
      : JSON.stringify(element) === JSON.stringify(value)
  );
};

export function groupBy(arr, key) {
  let result = arr.reduce((cumulator, value) => {
    let result = cumulator[value[key]] || [];
    result.push(value);
    cumulator[value[key]] = result;

    return cumulator;
  }, {});

  return result;
}

export const isEmpty = (obj) =>
  [Object, Array].includes((obj || {}).constructor) &&
  !Object.entries(obj || {}).length;

export const uniq = (array) => [...new Set(array)];

export function uniqBy(arr, key) {
  let entries = groupBy(arr, key);
  let result = [];
  for (let arr of Object.values(entries)) {
    result.push(arr[0]);
  }
  return result;
}

export function getCookie(name) {
  var v = document.cookie.match("(^|;) ?" + name + "=([^;]*)(;|$)");
  return v ? v[2] : null;
}

export async function getAllSystemFields(
  modelId = "System",
  serviceName = "User",
  refetch = false
) {
  const accountId = window.accountDetails._id;
  try {
    if (fieldsWithInternalCache[modelId] && !refetch) {
      return fieldsWithInternalCache[modelId];
    }
    serviceName = serviceName.toLowerCase();
    let url = `/${serviceName}/2/${accountId}/system/${modelId}/fields`;
    if (modelId === "User") {
      url = `/user/2/${accountId}/permissible/fields`;
    }
    let [promise] = DataService.get(url);
    let fieldList = await promise().then((fields) => fields);
    fieldsWithInternalCache[modelId] = fieldList;
    return fieldList;
  } catch (err) {
    console.warn("Failed to get all system fields", err);
    return [];
  }
}

export async function loadScript(id, path) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      return resolve();
    }
    const script = document.createElement("script");
    script.setAttribute("id", id);
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject();
    };
    script.src = path;
    document.head.appendChild(script);
  });
}

export function removeSpecialCharacters(input) {
  if (input.startsWith("_")) {
    input = input.slice(1);
    input = removeSpecialCharacters(input);
  }
  if (/[0-9]/.test(input[0])) {
    input = input.replace(/[0-9_]/, "");
    input = removeSpecialCharacters(input);
  }
  return input;
}

export function getIdFromString(input) {
  input = diacritics.remove(input); //"Iлｔèｒｎåｔïｏｎɑｌíƶａｔï߀ԉ" ==> "Internationalization"
  let unprintableChars =
    //eslint-disable-next-line
    /[\0-\x1F\x7F-\x9F\xAD\u0378\u0379\u037F-\u0383\u038B\u038D\u03A2\u0528-\u0530\u0557\u0558\u0560\u0588\u058B-\u058E\u0590\u05C8-\u05CF\u05EB-\u05EF\u05F5-\u0605\u061C\u061D\u06DD\u070E\u070F\u074B\u074C\u07B2-\u07BF\u07FB-\u07FF\u082E\u082F\u083F\u085C\u085D\u085F-\u089F\u08A1\u08AD-\u08E3\u08FF\u0978\u0980\u0984\u098D\u098E\u0991\u0992\u09A9\u09B1\u09B3-\u09B5\u09BA\u09BB\u09C5\u09C6\u09C9\u09CA\u09CF-\u09D6\u09D8-\u09DB\u09DE\u09E4\u09E5\u09FC-\u0A00\u0A04\u0A0B-\u0A0E\u0A11\u0A12\u0A29\u0A31\u0A34\u0A37\u0A3A\u0A3B\u0A3D\u0A43-\u0A46\u0A49\u0A4A\u0A4E-\u0A50\u0A52-\u0A58\u0A5D\u0A5F-\u0A65\u0A76-\u0A80\u0A84\u0A8E\u0A92\u0AA9\u0AB1\u0AB4\u0ABA\u0ABB\u0AC6\u0ACA\u0ACE\u0ACF\u0AD1-\u0ADF\u0AE4\u0AE5\u0AF2-\u0B00\u0B04\u0B0D\u0B0E\u0B11\u0B12\u0B29\u0B31\u0B34\u0B3A\u0B3B\u0B45\u0B46\u0B49\u0B4A\u0B4E-\u0B55\u0B58-\u0B5B\u0B5E\u0B64\u0B65\u0B78-\u0B81\u0B84\u0B8B-\u0B8D\u0B91\u0B96-\u0B98\u0B9B\u0B9D\u0BA0-\u0BA2\u0BA5-\u0BA7\u0BAB-\u0BAD\u0BBA-\u0BBD\u0BC3-\u0BC5\u0BC9\u0BCE\u0BCF\u0BD1-\u0BD6\u0BD8-\u0BE5\u0BFB-\u0C00\u0C04\u0C0D\u0C11\u0C29\u0C34\u0C3A-\u0C3C\u0C45\u0C49\u0C4E-\u0C54\u0C57\u0C5A-\u0C5F\u0C64\u0C65\u0C70-\u0C77\u0C80\u0C81\u0C84\u0C8D\u0C91\u0CA9\u0CB4\u0CBA\u0CBB\u0CC5\u0CC9\u0CCE-\u0CD4\u0CD7-\u0CDD\u0CDF\u0CE4\u0CE5\u0CF0\u0CF3-\u0D01\u0D04\u0D0D\u0D11\u0D3B\u0D3C\u0D45\u0D49\u0D4F-\u0D56\u0D58-\u0D5F\u0D64\u0D65\u0D76-\u0D78\u0D80\u0D81\u0D84\u0D97-\u0D99\u0DB2\u0DBC\u0DBE\u0DBF\u0DC7-\u0DC9\u0DCB-\u0DCE\u0DD5\u0DD7\u0DE0-\u0DF1\u0DF5-\u0E00\u0E3B-\u0E3E\u0E5C-\u0E80\u0E83\u0E85\u0E86\u0E89\u0E8B\u0E8C\u0E8E-\u0E93\u0E98\u0EA0\u0EA4\u0EA6\u0EA8\u0EA9\u0EAC\u0EBA\u0EBE\u0EBF\u0EC5\u0EC7\u0ECE\u0ECF\u0EDA\u0EDB\u0EE0-\u0EFF\u0F48\u0F6D-\u0F70\u0F98\u0FBD\u0FCD\u0FDB-\u0FFF\u10C6\u10C8-\u10CC\u10CE\u10CF\u1249\u124E\u124F\u1257\u1259\u125E\u125F\u1289\u128E\u128F\u12B1\u12B6\u12B7\u12BF\u12C1\u12C6\u12C7\u12D7\u1311\u1316\u1317\u135B\u135C\u137D-\u137F\u139A-\u139F\u13F5-\u13FF\u169D-\u169F\u16F1-\u16FF\u170D\u1715-\u171F\u1737-\u173F\u1754-\u175F\u176D\u1771\u1774-\u177F\u17DE\u17DF\u17EA-\u17EF\u17FA-\u17FF\u180F\u181A-\u181F\u1878-\u187F\u18AB-\u18AF\u18F6-\u18FF\u191D-\u191F\u192C-\u192F\u193C-\u193F\u1941-\u1943\u196E\u196F\u1975-\u197F\u19AC-\u19AF\u19CA-\u19CF\u19DB-\u19DD\u1A1C\u1A1D\u1A5F\u1A7D\u1A7E\u1A8A-\u1A8F\u1A9A-\u1A9F\u1AAE-\u1AFF\u1B4C-\u1B4F\u1B7D-\u1B7F\u1BF4-\u1BFB\u1C38-\u1C3A\u1C4A-\u1C4C\u1C80-\u1CBF\u1CC8-\u1CCF\u1CF7-\u1CFF\u1DE7-\u1DFB\u1F16\u1F17\u1F1E\u1F1F\u1F46\u1F47\u1F4E\u1F4F\u1F58\u1F5A\u1F5C\u1F5E\u1F7E\u1F7F\u1FB5\u1FC5\u1FD4\u1FD5\u1FDC\u1FF0\u1FF1\u1FF5\u1FFF\u200B-\u200F\u202A-\u202E\u2060-\u206F\u2072\u2073\u208F\u209D-\u209F\u20BB-\u20CF\u20F1-\u20FF\u218A-\u218F\u23F4-\u23FF\u2427-\u243F\u244B-\u245F\u2700\u2B4D-\u2B4F\u2B5A-\u2BFF\u2C2F\u2C5F\u2CF4-\u2CF8\u2D26\u2D28-\u2D2C\u2D2E\u2D2F\u2D68-\u2D6E\u2D71-\u2D7E\u2D97-\u2D9F\u2DA7\u2DAF\u2DB7\u2DBF\u2DC7\u2DCF\u2DD7\u2DDF\u2E3C-\u2E7F\u2E9A\u2EF4-\u2EFF\u2FD6-\u2FEF\u2FFC-\u2FFF\u3040\u3097\u3098\u3100-\u3104\u312E-\u3130\u318F\u31BB-\u31BF\u31E4-\u31EF\u321F\u32FF\u4DB6-\u4DBF\u9FCD-\u9FFF\uA48D-\uA48F\uA4C7-\uA4CF\uA62C-\uA63F\uA698-\uA69E\uA6F8-\uA6FF\uA78F\uA794-\uA79F\uA7AB-\uA7F7\uA82C-\uA82F\uA83A-\uA83F\uA878-\uA87F\uA8C5-\uA8CD\uA8DA-\uA8DF\uA8FC-\uA8FF\uA954-\uA95E\uA97D-\uA97F\uA9CE\uA9DA-\uA9DD\uA9E0-\uA9FF\uAA37-\uAA3F\uAA4E\uAA4F\uAA5A\uAA5B\uAA7C-\uAA7F\uAAC3-\uAADA\uAAF7-\uAB00\uAB07\uAB08\uAB0F\uAB10\uAB17-\uAB1F\uAB27\uAB2F-\uABBF\uABEE\uABEF\uABFA-\uABFF\uD7A4-\uD7AF\uD7C7-\uD7CA\uD7FC-\uF8FF\uFA6E\uFA6F\uFADA-\uFAFF\uFB07-\uFB12\uFB18-\uFB1C\uFB37\uFB3D\uFB3F\uFB42\uFB45\uFBC2-\uFBD2\uFD40-\uFD4F\uFD90\uFD91\uFDC8-\uFDEF\uFDFE\uFDFF\uFE1A-\uFE1F\uFE27-\uFE2F\uFE53\uFE67\uFE6C-\uFE6F\uFE75\uFEFD-\uFF00\uFFBF-\uFFC1\uFFC8\uFFC9\uFFD0\uFFD1\uFFD8\uFFD9\uFFDD-\uFFDF\uFFE7\uFFEF-\uFFFB\uFFFE\uFFFF]/g;
  return input
    .trim()
    .replace(/ /g, "_")
    .replace(/\W+/g, "")
    .replaceAll(unprintableChars, "");
}

export function useDocumentVisibility() {
  const [isDocumentVisible, setIsDocumentVisible] = useState(true);

  useEffect(function initializeObserver() {
    function onDocumentFocus() {
      setIsDocumentVisible(document.visibilityState === "visible");
    }

    document.addEventListener("visibilitychange", onDocumentFocus);

    return () => {
      document.removeEventListener("visibilitychange", onDocumentFocus);
    };
  }, []);

  return { isDocumentVisible };
}

export const checkIsDownloadDisable = (file) => {
  const filePreviewConfigData =
    useFilePreviewStore?.getState().filePreviewConfig || {};
  let matchedFlowDetails = {};
  file &&
    Object.keys(filePreviewConfigData).some((id) => {
      if (file.key.includes(id)) {
        matchedFlowDetails = filePreviewConfigData[id];
        return true;
      }
      return false;
    });
  return matchedFlowDetails.disableDownload;
};

export const checkIsPrintDisable = (file) => {
  const filePreviewConfigData =
    useFilePreviewStore?.getState().filePreviewConfig || {};
  let matchedFlowDetails = {};
  file &&
    Object.keys(filePreviewConfigData).some((id) => {
      if (file.key.includes(id)) {
        matchedFlowDetails = filePreviewConfigData[id];
        return true;
      }
      return false;
    });
  return matchedFlowDetails.disablePrint;
};

export function getGeoLocationKey() {
  let { Geolocation = null } = useAccountStore.getState().AppConfig;
  if (Geolocation && Geolocation.hasOwnProperty("GeolocationApiKey")) {
    return Geolocation["GeolocationApiKey"];
  }
  return "";
}

export function constructPublicformUrl(formId, flowType) {
  const endPoint = useAccountStore.getState().Endpoint;
  return `https://${endPoint}/public/${flowType}/${formId}`;
}

export async function getSandbox(accountDetails) {
  const { Features = [] } = accountDetails || {};

  const isWorkPlatform = Features.includes(
    WORK_PLATFORM_FEATURE_FLAGS.ALLOW_WORK_PLATFORM_NAVIGATION
  );
  const isAdminAllowSandbox = Features.includes(
    WORK_PLATFORM_FEATURE_FLAGS.ADMIN_ALLOW_SANDBOX
  );

  if (isWorkPlatform && isAdminAllowSandbox) {
    useUserStore.getState().fetchAndUpdateSandbox();
  } else {
    const sandboxResp = [
      {
        _id: useAccountStore.getState()._id,
        Type: ENVIRONMENT_TYPES.PRODUCTION
      }
    ];
    useUserStore.getState().setSandbox(sandboxResp);
  }
}

export const getFilterPayload = (type = ENVIRONMENT_TYPES.PRODUCTION) => {
  let {
    PROCESS,
    CASE,
    DATASET,
    PROJECT,
    APPLICATION,
    PORTAL,
    FORM,
    LIST,
    INTEGRATION,
    DATASET_VIEW
  } = FLOW_TYPE;

  let { FORMVIEW, CASEVIEW, REPORT, ANALYTICS_REPORT } = SUB_FLOW_TYPES;

  return {
    Filter: {
      LHSField: "Type",
      RHSValue:
        type === ENVIRONMENT_TYPES.PRODUCTION
          ? [
              PROCESS,
              CASE,
              DATASET,
              LIST,
              PROJECT,
              APPLICATION,
              PORTAL,
              FORM,
              INTEGRATION,
              FORMVIEW,
              CASEVIEW,
              REPORT,
              ANALYTICS_REPORT,
              DATASET_VIEW
            ].toString()
          : [APPLICATION, PORTAL, DATASET].toString(),
      Operator: "PART_OF"
    }
  };
};

export const generateFilePreviewData = (file, isS3FileKey = false, flowId) => {
  return {
    name: file.name,
    fileExtension: file.fileExtension,
    key: isS3FileKey ? getS3FileKey(file.key, flowId) : file.key,
    ...(file.duration && { duration: file.duration }),
    ...(file.size && { size: file.size }),
    ...(file.isProfile && { isProfile: file.isProfile })
  };
};

export const delayTime = (ms) => {
  return new Promise((res) => setTimeout(res, ms));
};

export function exponentialBackoff(
  fn,
  { delay = 100, maxRetries = 3, onMaxRetriesReached = () => {} }
) {
  let retryCount = 0;
  let isCancelled = false;

  // NOTE: used to stop backoff call
  const stop = () => {
    isCancelled = true;
  };

  const backoffFunction = async () => {
    while (retryCount < maxRetries) {
      try {
        if (isCancelled) {
          console.info("Backoff logic stopped");
          return;
        }
        return await fn();
      } catch (error) {
        retryCount++;
        if (retryCount >= maxRetries) {
          console.info("Max retries reached");
          onMaxRetriesReached();
          return; // Max retries reached
        }
        await delayTime(2 ** retryCount * delay);
      }
    }
  };
  backoffFunction.stop = stop; // Expose the stop function
  return backoffFunction;
}

export const isRunningAsStandaloneApp = () => {
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }
  return false;
};

/**
 * Retrieves the form meta limit configuration.
 *
 * This function returns an object containing limits for the form metadata
 * based on the provided sys config from flow info response or default values if not specified.
 * @param {number} [flowInfo.SysConfig.MaxFieldLimit] - The maximum number of fields allowed. Defaults to DEFAULT_FORM_META_LIMIT.MaxFieldLimit.
 * @param {number} [flowInfo.SysConfig.MaxChildTableLimit] - The maximum number of child tables allowed. Defaults to DEFAULT_FORM_META_LIMIT.MaxChildTableLimit.
 * @param {number} [flowInfo.SysConfig.MaxChildTableColumnLimit] - The maximum number of columns in child tables allowed. Defaults to DEFAULT_FORM_META_LIMIT.MaxChildTableColumnLimit.
 * @returns {Object} The form meta limit configuration.
 */
export const getFormMetaLimitConfig = (flowInfo = {}) => {
  let {
    MaxFieldLimit = DEFAULT_FORM_META_LIMIT.MaxFieldLimit,
    MaxChildTableLimit = DEFAULT_FORM_META_LIMIT.MaxChildTableLimit,
    MaxChildTableColumnLimit = DEFAULT_FORM_META_LIMIT.MaxChildTableColumnLimit,
    MaxCustomFieldLimit = DEFAULT_FORM_META_LIMIT.MaxCustomFieldLimit
  } = flowInfo?.SysConfig || {};
  let formMetaLimit = {
    MaxCustomFieldLimit,
    MaxFieldLimit,
    MaxChildTableLimit,
    MaxChildTableColumnLimit
  };
  return formMetaLimit;
};

/*
 * Based on the flowInfo Details this method will return customized field specific limit or the default limit
 */
export const getFieldLimitConfig = (flowInfo = {}) => {
  let fieldLimit = flowInfo?.SysConfig?.FieldLimit || {};
  let fieldSpecificLimit = Object.assign({}, DEFAULT_FIELD_LIMIT, fieldLimit);
  return fieldSpecificLimit;
};

export const showErrorMessage = async (error) => {
  const feedback = () => import("widgets/feedback");
  const { Toast } = await feedback();
  const { showError } = Toast;
  showError({
    message:
      i18n._(error.message, error.args || {}) || i18n._(getErrorText("DEFAULT"))
  });
};

export function getPlatformType() {
  // eslint-disable-next-line no-undef
  return PLATFORM_TYPE;
}

/**
 * Used to mark deleted objects comparing two Arrays
 * Example:
 *  originalArray=[{_id:"1",Name:"One"},{_id:"2",Name:"Two"}]
 *  newArray=[{_id:"1",Name:"One"}]
 *  returns [{_id:"1",Name:"One"},{_id:"2",Name:"Two", isDelete:true}]
 * @param {object[]} originalArray
 * @param {object[]} newArray
 * @param {String} compareBy
 * @returns {object[]}
 */
export function markDeletedItems(originalArray, newArray, compareBy = "_id") {
  const originalSet = new Set(originalArray.map((obj) => obj[compareBy]));
  const newSet = new Set(newArray.map((obj) => obj[compareBy]));

  const addedItems = newArray.reduce((accumulator, currentValue) => {
    if (!originalSet.has(currentValue[compareBy])) {
      accumulator.push({
        _id: currentValue._id,
        Name: currentValue.Name
      });
    }
    return accumulator;
  }, []);

  const removedItems = originalArray.reduce((accumulator, currentValue) => {
    if (!newSet.has(currentValue[compareBy])) {
      accumulator.push({
        _id: currentValue._id,
        Name: currentValue.Name,
        IsDelete: true
      });
    }
    return accumulator;
  }, []);

  return addedItems.concat(removedItems);
}

/**
 * this function is used to convert base64 string to blob
 * Example:
 * @param {string} base64Data
 * @param {string} contentType,
 * @returns {blob object}
 */
export const base64toBlob = (base64Data, contentType = "") => {
  let sliceSize = 1024;
  let byteCharacters = atob(base64Data);
  let bytesLength = byteCharacters.length;
  let slicesCount = Math.ceil(bytesLength / sliceSize);
  let byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    let begin = sliceIndex * sliceSize;
    let end = Math.min(begin + sliceSize, bytesLength);

    let bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
};

export function isObject(item) {
  return typeof item === "object" && !Array.isArray(item) && item !== null;
}

export function getStyleForColor(color) {
  if (!color) return null;
  return {
    "Field.Color": {
      ref: OLD_TO_NEW_COLOR_MAPPING[color]
    }
  };
}

/**
 * This function convert first list of objects to object , where every item's key depends on key param
 * @param {Array} list
 * @param {string} key
 * @returns {object}
 */
export function toObject(list, key) {
  let initialValue = {};
  return list.reduce(function getObject(accumulator, currentValue) {
    accumulator[currentValue[key]] = currentValue;
    return accumulator;
  }, initialValue);
}

export function getFieldIconName(field) {
  return field.Widget === WIDGET_TYPES.CUSTOM
    ? WIDGET_TYPES.CUSTOM
    : field.Type;
}

export function getEnvironment(
  cb = (env) => env.Type === ENVIRONMENT_TYPES.PRODUCTION
) {
  return useUserStore.getState().sandbox.find(cb) || {};
}

export function isProxyEnvironment(accountId = window.accountDetails._id) {
  return window.accountDetails._id !== accountId;
}

export function canAuthenticateSandbox() {
  const parentAccount = useAccountStore.getState().ParentAccount;
  const isSandboxAccount = !isEmpty(parentAccount);
  return isSandboxAccount && !isLocalhost();
}
